import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy,
  DownloadSimple,
  Envelope,
  ArrowLeft,
  Spinner,
  WarningOctagon,
  Compass,
  Handshake,
  Quotes,
  Path,
  Scales,
  UserFocus,
  ChatCircleText,
  ClockClockwise,
  ShieldWarning,
  Target,
  UserCircle,
  ListChecks,
} from "@phosphor-icons/react";
import { api, API } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";

const SectionCard = ({ icon: Icon, title, children, testid, accent = "#1E3A2B" }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#E8E4DB] rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-all"
  >
    <div className="flex items-center gap-2 label-kicker" style={{ color: accent }}>
      <Icon size={16} weight="duotone" /> {title}
    </div>
    <div className="mt-4 text-[#4A524C] leading-relaxed">{children}</div>
  </div>
);

function GeneratePlanPrompt({ id, onDone }) {
  const [busy, setBusy] = React.useState(false);
  const nav = useNavigate();
  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/walkthrough/${id}/plan`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate plan");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-2xl mx-auto px-6 py-16 lg:py-24">
      <div className="bg-white border border-[#E8E4DB] rounded-2xl p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-[#F3F0EA] flex items-center justify-center mx-auto">
          <Compass size={28} weight="duotone" color="#1E3A2B" />
        </div>
        <h2 className="font-serif text-3xl text-[#1E3A2B] mt-4">Generate your full action plan</h2>
        <p className="mt-3 text-[#4A524C] leading-relaxed">
          We'll turn your walkthrough conversation into a comprehensive Leadership Conflict Action Plan with stakes, risks, scripts, and escalation options — ready to download, copy, or email.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <button
            onClick={() => nav(`/walkthrough/${id}`)}
            data-testid="report-back-to-walkthrough"
            className="px-5 py-2.5 rounded-xl bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#121815]"
          >
            Back to walkthrough
          </button>
          <button
            onClick={generate}
            disabled={busy}
            data-testid="report-generate-plan-button"
            className="px-6 py-2.5 rounded-xl bg-[#1E3A2B] text-white hover:bg-[#2C4C3B] disabled:opacity-60 inline-flex items-center gap-2"
          >
            {busy ? <Spinner size={16} className="animate-spin" /> : null}
            Generate plan
          </button>
        </div>
        <div className="mt-6 text-xs text-[#8A948E]">Typically ~30 seconds</div>
      </div>
    </div>
  );
}

const Bulleted = ({ items }) => (
  <ul className="space-y-2">
    {items?.map((t, i) => (
      <li key={i} className="flex gap-2">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#768C7E] flex-shrink-0" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const EXPAND_META = {
  expand: { label: "Expand My Plan", icon: Path },
  draft_response: { label: "Draft Better Response", icon: Quotes },
  prepare: { label: "Prepare Full Conversation", icon: Handshake },
};

export default function Report() {
  const { id } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailAddr, setEmailAddr] = useState("");
  const [emailing, setEmailing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/walkthrough/${id}`);
      setSession(data);
    } catch (e) {
      toast.error("Could not load plan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const runAction = async (key) => {
    setActionLoading(key);
    try {
      await api.post(`/walkthrough/${id}/action/${key}`);
      await load();
      toast.success(`${EXPAND_META[key].label} ready`);
    } catch (e) {
      toast.error("Could not expand");
    } finally {
      setActionLoading(null);
    }
  };

  const copyPlan = async () => {
    if (!session?.plan) return;
    const s = session.plan;
    const bl = (arr) => (arr || []).map((x) => `- ${x}`);
    const text = [
      "LEADERSHIP CONFLICT ACTION PLAN",
      `Ref ${id.slice(0, 8).toUpperCase()}  ·  ${new Date(session.created_at).toLocaleString()}`,
      "",
      "1. CONFLICT OVERVIEW",
      s.conflict_overview,
      "",
      "2. NEUTRAL DESCRIPTION",
      s.neutral_description,
      "",
      "3. IMMEDIATE LEADERSHIP CHALLENGES",
      ...bl(s.immediate_leadership_challenges),
      "",
      "4. CORE NEEDS & INTERESTS",
      ...bl(s.core_needs_and_interests),
      "",
      "5. LIKELY OTHER-PARTY NEEDS",
      ...bl(s.other_party_needs),
      "",
      "6. STRATEGIC STAKES",
      ...bl(s.strategic_stakes),
      "",
      "7. ORGANIZATIONAL RISKS",
      ...bl(s.organizational_risks),
      "",
      "8. HR / LEGAL / COMPLIANCE FLAGS",
      ...bl(s.hr_legal_compliance_flags),
      "",
      "9. RECOMMENDED LEADERSHIP STRATEGY",
      s.leadership_strategy,
      "",
      "10. SUGGESTED COMMUNICATION SCRIPTS",
      ...bl(s.communication_scripts),
      "",
      "11. COLLABORATIVE SOLUTION PATHWAYS",
      ...bl(s.collaborative_pathways),
      "",
      "12. ESCALATION RECOMMENDATIONS",
      ...bl(s.escalation_recommendations),
      ...(s.human_coach_recommendation
        ? ["", "13. HUMAN COACHING RECOMMENDATION", s.human_coach_recommendation]
        : []),
      "",
      "—",
      "Conflict Coach for Leaders provides strategic conflict guidance. It does not provide legal, HR, financial, compliance, medical, or psychological advice.",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Plan copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/walkthrough/${id}/report.pdf`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `leadership-action-plan-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // revoke after a tick so the click has time to start the download
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download PDF");
    }
  };

  const sendEmail = async () => {
    if (!emailAddr) return;
    setEmailing(true);
    try {
      await api.post(`/walkthrough/${id}/email`, { to_email: emailAddr });
      toast.success(`Emailed to ${emailAddr}`);
      setEmailOpen(false);
      setEmailAddr("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setEmailing(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center text-[#4A524C]">
        <Spinner size={28} className="animate-spin mx-auto" />
        <div className="mt-3">Loading your plan…</div>
      </div>
    );
  }

  if (!session) return null;

  // If plan is not yet generated, offer to generate it.
  if (!session.plan) {
    return <GeneratePlanPrompt id={id} onDone={load} />;
  }

  const s = session.plan;

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10 lg:py-14">
      <div className="flex items-center justify-between gap-3 anim-in delay-1">
        <Link
          to="/walkthrough"
          data-testid="report-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#4A524C] hover:text-[#1E3A2B]"
        >
          <ArrowLeft size={16} weight="bold" /> New walkthrough
        </Link>
        <div className="text-xs text-[#8A948E] font-mono">
          {new Date(session.created_at).toLocaleString()}
        </div>
      </div>

      <div className="mt-6 anim-in delay-2">
        <div className="label-kicker">Leadership Conflict Action Plan</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1E3A2B] tracking-tight leading-tight mt-3">
          {(s.conflict_overview || "").split(".")[0]}.
        </h1>
      </div>

      {/* Action toolbar */}
      <div className="mt-8 flex flex-wrap gap-2 anim-in delay-3">
        <button
          onClick={copyPlan}
          data-testid="report-copy-button"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E8E4DB] text-[#4A524C] hover:text-[#1E3A2B] hover:border-[#1E3A2B] text-sm transition-all"
        >
          <Copy size={16} weight="duotone" /> Copy Plan
        </button>
        <button
          onClick={downloadPdf}
          data-testid="report-download-button"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E8E4DB] text-[#4A524C] hover:text-[#1E3A2B] hover:border-[#1E3A2B] text-sm transition-all"
        >
          <DownloadSimple size={16} weight="duotone" /> Download PDF
        </button>
        <button
          onClick={() => setEmailOpen(true)}
          data-testid="report-email-button"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E8E4DB] text-[#4A524C] hover:text-[#1E3A2B] hover:border-[#1E3A2B] text-sm transition-all"
        >
          <Envelope size={16} weight="duotone" /> Email Plan
        </button>
        <button
          onClick={() => nav("/coach")}
          data-testid="report-contact-coach-button"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A2B] text-white text-sm hover:bg-[#2C4C3B] transition-all"
        >
          Contact Human Coach
        </button>
      </div>

      {/* 1. Conflict Overview */}
      <div className="mt-10 anim-in delay-4">
        <SectionCard icon={Compass} title="01 · Conflict Overview" testid="section-overview">
          <p className="text-lg text-[#121815] leading-relaxed">{s.conflict_overview}</p>
        </SectionCard>
      </div>

      {/* 2. Neutral Description */}
      <div className="mt-5">
        <SectionCard icon={ChatCircleText} title="02 · Neutral Description" testid="section-neutral">
          <p>{s.neutral_description}</p>
        </SectionCard>
      </div>

      {/* 3. Immediate Leadership Challenges */}
      <div className="mt-5">
        <SectionCard icon={Target} title="03 · Immediate Leadership Challenges" testid="section-challenges">
          <Bulleted items={s.immediate_leadership_challenges} />
        </SectionCard>
      </div>

      {/* 4-5. Needs (two-up) */}
      <div className="mt-5 grid md:grid-cols-2 gap-5">
        <SectionCard icon={UserFocus} title="04 · Core Needs & Interests" testid="section-your-needs">
          <Bulleted items={s.core_needs_and_interests} />
        </SectionCard>
        <SectionCard icon={UserFocus} title="05 · Likely Other-Party Needs" testid="section-other-needs">
          <Bulleted items={s.other_party_needs} />
        </SectionCard>
      </div>

      {/* 6-7. Stakes + Risks (two-up) */}
      <div className="mt-5 grid md:grid-cols-2 gap-5">
        <SectionCard icon={Scales} title="06 · Strategic Stakes" testid="section-strategic-stakes">
          <Bulleted items={s.strategic_stakes} />
        </SectionCard>
        <SectionCard icon={WarningOctagon} title="07 · Organizational Risks" testid="section-risks" accent="#C77D5E">
          <Bulleted items={s.organizational_risks} />
        </SectionCard>
      </div>

      {/* 8. HR/Legal/Compliance */}
      <div className="mt-5">
        <SectionCard icon={ShieldWarning} title="08 · HR · Legal · Compliance Flags" testid="section-hrlegal" accent="#C77D5E">
          <Bulleted items={s.hr_legal_compliance_flags} />
          <p className="mt-3 text-xs text-[#8A948E]">
            These are flags for awareness only. Consult qualified professional counsel when indicated.
          </p>
        </SectionCard>
      </div>

      {/* 9. Leadership Strategy */}
      <div className="mt-5">
        <SectionCard icon={ListChecks} title="09 · Recommended Leadership Strategy" testid="section-strategy">
          <p className="text-lg text-[#121815] leading-relaxed">{s.leadership_strategy}</p>
        </SectionCard>
      </div>

      {/* 10. Communication Scripts */}
      <div className="mt-5">
        <SectionCard icon={Quotes} title="10 · Suggested Communication Scripts" testid="section-scripts">
          <div className="space-y-4">
            {(s.communication_scripts || []).map((raw, i) => {
              const splitIdx = raw.indexOf(" — ");
              const fallbackIdx = splitIdx === -1 ? raw.indexOf(" - ") : splitIdx;
              const hasLabel = fallbackIdx > 0 && fallbackIdx < 60;
              const label = hasLabel ? raw.slice(0, fallbackIdx).trim() : `Script ${i + 1}`;
              const body = hasLabel ? raw.slice(fallbackIdx + 3).trim() : raw;
              return (
                <div key={i} data-testid={`script-${i}`}>
                  <div className="label-kicker mb-2">{label}</div>
                  <p className="italic text-[#121815] leading-relaxed border-l-2 border-[#1E3A2B] pl-4 bg-[#F3F0EA] py-3 pr-3 rounded-r-lg">
                    &ldquo;{body}&rdquo;
                  </p>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* 11-12. Collaborative + Escalation (two-up) */}
      <div className="mt-5 grid md:grid-cols-2 gap-5">
        <SectionCard icon={Path} title="11 · Collaborative Solution Pathways" testid="section-collab">
          <Bulleted items={s.collaborative_pathways} />
        </SectionCard>
        <SectionCard icon={ClockClockwise} title="12 · Escalation Recommendations" testid="section-escalation" accent="#C77D5E">
          <Bulleted items={s.escalation_recommendations} />
        </SectionCard>
      </div>

      {/* 13. Human Coach — only if populated */}
      {s.human_coach_recommendation && (
        <div className="mt-5">
          <SectionCard icon={UserCircle} title="13 · Human Coaching Recommendation" testid="section-human-coach">
            <p className="text-[#121815] leading-relaxed">{s.human_coach_recommendation}</p>
            <button
              onClick={() => nav("/coach")}
              data-testid="section-human-coach-cta"
              className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#1E3A2B] text-white text-sm hover:bg-[#2C4C3B] transition-all"
            >
              <Handshake size={16} weight="duotone" /> Connect with a coach
            </button>
          </SectionCard>
        </div>
      )}

      {/* Expansion buttons */}
      <div className="mt-10 border-t border-[#E8E4DB] pt-8">
        <div className="label-kicker">Go Deeper</div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          {Object.entries(EXPAND_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const has = session.expansions && session.expansions[key];
            return (
              <button
                key={key}
                onClick={() => runAction(key)}
                disabled={actionLoading === key}
                data-testid={`expand-${key}-button`}
                className="text-left bg-white border border-[#E8E4DB] rounded-2xl p-5 hover:border-[#1E3A2B] hover:shadow-md transition-all disabled:opacity-60"
              >
                <div className="flex items-center gap-2">
                  <Icon size={20} weight="duotone" color="#1E3A2B" />
                  <span className="font-medium text-[#121815]">{meta.label}</span>
                </div>
                <div className="mt-2 text-xs text-[#8A948E]">
                  {actionLoading === key
                    ? "Generating…"
                    : has
                    ? "Click to regenerate"
                    : "Generate"}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Expansions content */}
      {session.expansions && Object.keys(session.expansions).length > 0 && (
        <div className="mt-10 space-y-6">
          {Object.entries(session.expansions).map(([key, content]) => (
            <div
              key={key}
              data-testid={`expansion-${key}`}
              className="bg-white border border-[#E8E4DB] rounded-2xl p-6 sm:p-8"
            >
              <div className="label-kicker">{EXPAND_META[key]?.label || key}</div>
              <div
                className="mt-4 prose-coach"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Email dialog */}
      {emailOpen && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50"
          onClick={() => setEmailOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-8 max-w-md w-full border border-[#E8E4DB]"
          >
            <h3 className="font-serif text-2xl text-[#1E3A2B]">Email this plan</h3>
            <p className="text-sm text-[#4A524C] mt-2">PDF will be attached.</p>
            <input
              data-testid="email-report-input"
              type="email"
              value={emailAddr}
              onChange={(e) => setEmailAddr(e.target.value)}
              placeholder="you@company.com"
              className="mt-4 w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:outline-none rounded-xl px-4 py-3"
            />
            <div className="mt-5 flex gap-2 justify-end">
              <button
                onClick={() => setEmailOpen(false)}
                className="px-5 py-2.5 rounded-xl bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#121815]"
              >
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={!emailAddr || emailing}
                data-testid="email-report-send"
                className="px-5 py-2.5 rounded-xl bg-[#1E3A2B] text-white hover:bg-[#2C4C3B] disabled:opacity-60 inline-flex items-center gap-2"
              >
                {emailing ? <Spinner size={16} className="animate-spin" /> : <Envelope size={16} weight="fill" />}
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
